import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import BACKEND_URL from '../../vnog/config';

interface HumanSuggestedMessageItem {
    id: number;
    message: string;
    created_at: string;
    event_id: number;
    documentation_name: string | null;
    quote: { exact_document_quotes?: string[] } | null;
}

interface EventData {
    id: number;
    order?: number;
    start_time?: string;
    end_time?: string;
    event_type: string;
    message_content?: string;
    message_sender?: string;
    other_arguments?: any;
}

interface HumanSuggestedMessageDetail {
    id: number;
    message: string;
    created_at: string;
    ai_operator_message_type: string;
    quote: { exact_document_quotes?: string[] } | null;
    documentation: {
        id: number;
        summary_name: string | null;
        summary: string | null;
        document_id: string;
    } | null;
    event: EventData | null;
    last_seen_event: EventData | null;
    characteristics_estimation_event: EventData | null;
    documentation_search_event: EventData | null;
    simulation_data: {
        id: number;
        token: string;
        created_at: string;
        status: string;
        address?: {
            id: number;
            street: string;
            zipcode: string;
            house_number: string;
        };
        event_log: EventData[];
    } | null;
}

interface HumanSuggestedMessagesProps {
    onLoadingChange?: (loading: boolean) => void;
}

const DetailView: React.FC<{ detail: HumanSuggestedMessageDetail | null }> = ({ detail }) => {
    if (!detail) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="text-gray-500 text-center">Select a message to view details</div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Human Suggested Message Details</h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left column - Main details */}
                <div className="space-y-4">
                    <div>
                        <div className="text-sm text-gray-500 mb-1">Message</div>
                        <div className="text-sm text-gray-700 break-words bg-blue-50 p-3 rounded border border-blue-200">
                            {detail.message}
                        </div>
                    </div>

                    {detail.quote?.exact_document_quotes && detail.quote.exact_document_quotes.length > 0 && (
                        <div>
                            <div className="text-sm text-gray-500 mb-1">Quote</div>
                            {detail.quote.exact_document_quotes.map((q, i) => (
                                <div key={i} className="text-sm text-gray-700 italic bg-yellow-50 p-3 rounded border border-yellow-200 mb-1">
                                    &quot;{q}&quot;
                                </div>
                            ))}
                        </div>
                    )}

                    <div>
                        <div className="text-sm text-gray-500 mb-1">Created At</div>
                        <div className="text-sm text-gray-700">{new Date(detail.created_at).toLocaleString()}</div>
                    </div>

                    {detail.documentation && (
                        <div>
                            <div className="text-sm text-gray-500 mb-1">Documentation</div>
                            <div className="bg-gray-50 p-3 rounded border border-gray-200">
                                <div className="text-sm font-medium text-gray-900 mb-1">
                                    {detail.documentation.summary_name || 'Unnamed'}
                                </div>
                                {detail.documentation.summary && (
                                    <div className="text-xs text-gray-600 max-h-32 overflow-y-auto">
                                        {detail.documentation.summary}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {detail.event && (
                        <div>
                            <div className="text-sm text-gray-500 mb-1">AI Operator Event</div>
                            <div className="bg-blue-50 p-3 rounded border border-blue-200">
                                <div className="text-sm"><strong>Event ID:</strong> {detail.event.id}</div>
                                <div className="text-sm"><strong>Type:</strong> {detail.event.event_type}</div>
                                {detail.event.message_content && (
                                    <div className="text-sm"><strong>Message:</strong> {detail.event.message_content}</div>
                                )}
                            </div>
                        </div>
                    )}

                    {detail.last_seen_event && (
                        <div>
                            <div className="text-sm text-gray-500 mb-1">Last Seen Event</div>
                            <div className="bg-orange-50 p-3 rounded border border-orange-200">
                                <div className="text-sm"><strong>Event ID:</strong> {detail.last_seen_event.id}</div>
                                <div className="text-sm"><strong>Type:</strong> {detail.last_seen_event.event_type}</div>
                                {detail.last_seen_event.message_content && (
                                    <div className="text-sm"><strong>Message:</strong> {detail.last_seen_event.message_content}</div>
                                )}
                                {detail.last_seen_event.message_sender && (
                                    <div className="text-sm"><strong>Sender:</strong> {detail.last_seen_event.message_sender}</div>
                                )}
                            </div>
                        </div>
                    )}

                    {detail.characteristics_estimation_event && (
                        <div>
                            <div className="text-sm text-gray-500 mb-1">Characteristics Estimation Event</div>
                            <div className="bg-purple-50 p-3 rounded border border-purple-200">
                                <div className="text-sm"><strong>Event ID:</strong> {detail.characteristics_estimation_event.id}</div>
                                <div className="text-sm"><strong>Type:</strong> {detail.characteristics_estimation_event.event_type}</div>
                            </div>
                        </div>
                    )}

                    {detail.documentation_search_event && (
                        <div>
                            <div className="text-sm text-gray-500 mb-1">Documentation Search Event</div>
                            <div className="bg-green-50 p-3 rounded border border-green-200">
                                <div className="text-sm"><strong>Event ID:</strong> {detail.documentation_search_event.id}</div>
                                <div className="text-sm"><strong>Type:</strong> {detail.documentation_search_event.event_type}</div>
                            </div>
                        </div>
                    )}

                    {detail.simulation_data && (
                        <div>
                            <div className="text-sm text-gray-500 mb-1">Simulation Details</div>
                            <div className="bg-gray-50 p-3 rounded border border-gray-200">
                                <div className="text-sm"><strong>Simulation ID:</strong> {detail.simulation_data.id}</div>
                                <div className="text-sm"><strong>Status:</strong> {detail.simulation_data.status}</div>
                                <div className="text-sm"><strong>Created:</strong> {new Date(detail.simulation_data.created_at).toLocaleString()}</div>
                                {detail.simulation_data.address && (
                                    <div className="text-sm">
                                        <strong>Address:</strong> {detail.simulation_data.address.street} {detail.simulation_data.address.house_number}, {detail.simulation_data.address.zipcode}
                                    </div>
                                )}
                                <div className="text-sm"><strong>Events:</strong> {detail.simulation_data.event_log.length} events</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right column - Event log */}
                {detail.simulation_data && detail.simulation_data.event_log && detail.simulation_data.event_log.length > 0 && (
                    <div>
                        <div className="text-sm text-gray-500 mb-1">Event Log</div>
                        <div className="bg-gray-50 p-3 rounded max-h-[600px] overflow-y-auto">
                            <div className="space-y-2">
                                {detail.simulation_data.event_log.map((event, index) => {
                                    const isTargetEvent = detail.event && event.id === detail.event.id;
                                    const isLastSeenEvent = detail.last_seen_event && event.id === detail.last_seen_event.id;
                                    const isCEEvent = detail.characteristics_estimation_event && event.id === detail.characteristics_estimation_event.id;
                                    const isDSEvent = detail.documentation_search_event && event.id === detail.documentation_search_event.id;

                                    return (
                                        <div
                                            key={event.id}
                                            className={`p-3 rounded border ${
                                                isTargetEvent
                                                    ? 'bg-blue-100 border-blue-300 ring-2 ring-blue-400'
                                                    : isLastSeenEvent
                                                    ? 'bg-orange-100 border-orange-300 ring-2 ring-orange-400'
                                                    : isCEEvent
                                                    ? 'bg-purple-100 border-purple-300 ring-1 ring-purple-400'
                                                    : isDSEvent
                                                    ? 'bg-green-100 border-green-300 ring-1 ring-green-400'
                                                    : 'bg-white border-gray-200'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                                                    <span className={`text-xs px-2 py-1 rounded ${
                                                        isTargetEvent
                                                            ? 'bg-blue-200 text-blue-800'
                                                            : isLastSeenEvent
                                                            ? 'bg-orange-200 text-orange-800'
                                                            : 'bg-gray-200 text-gray-700'
                                                    }`}>
                                                        #{index + 1}
                                                    </span>
                                                    <span className={`text-xs px-2 py-1 rounded ${
                                                        event.event_type === 'ai_operator'
                                                            ? 'bg-green-100 text-green-800'
                                                            : event.event_type === 'message'
                                                            ? 'bg-purple-100 text-purple-800'
                                                            : event.event_type === 'characteristics_estimation'
                                                            ? 'bg-indigo-100 text-indigo-800'
                                                            : event.event_type === 'documentation_search'
                                                            ? 'bg-teal-100 text-teal-800'
                                                            : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {event.event_type}
                                                    </span>
                                                    {isTargetEvent && (
                                                        <span className="text-xs px-2 py-1 rounded bg-blue-200 text-blue-800 font-medium">
                                                            Suggested Message
                                                        </span>
                                                    )}
                                                    {isLastSeenEvent && (
                                                        <span className="text-xs px-2 py-1 rounded bg-orange-200 text-orange-800 font-medium">
                                                            Last Seen
                                                        </span>
                                                    )}
                                                    {isCEEvent && (
                                                        <span className="text-xs px-2 py-1 rounded bg-purple-200 text-purple-800 font-medium">
                                                            Char. Estimation
                                                        </span>
                                                    )}
                                                    {isDSEvent && (
                                                        <span className="text-xs px-2 py-1 rounded bg-green-200 text-green-800 font-medium">
                                                            Doc Search
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    ID: {event.id}
                                                </div>
                                            </div>

                                            {event.message_content && (
                                                <div className="mb-2">
                                                    <div className="text-xs text-gray-500 mb-1">Message:</div>
                                                    <div className="text-sm text-gray-700 break-words">{event.message_content}</div>
                                                </div>
                                            )}

                                            {event.message_sender && (
                                                <div className="mb-2">
                                                    <div className="text-xs text-gray-500 mb-1">Sender:</div>
                                                    <div className="text-sm text-gray-700">{event.message_sender}</div>
                                                </div>
                                            )}

                                            {event.other_arguments && Object.keys(event.other_arguments).length > 0 && (
                                                <div className="mb-2">
                                                    <div className="text-xs text-gray-500 mb-1">Additional Data:</div>
                                                    <div className="text-xs text-gray-600 bg-gray-100 p-2 rounded">
                                                        <pre className="whitespace-pre-wrap break-words">
                                                            {JSON.stringify(event.other_arguments, null, 2)}
                                                        </pre>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                {event.start_time && (
                                                    <div>Start: {new Date(event.start_time).toLocaleString()}</div>
                                                )}
                                                {event.end_time && (
                                                    <div>End: {new Date(event.end_time).toLocaleString()}</div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const HumanSuggestedMessages: React.FC<HumanSuggestedMessagesProps> = ({ onLoadingChange }) => {
    const [messages, setMessages] = useState<HumanSuggestedMessageItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedMessageId, setSelectedMessageId] = useState<number | null>(null);
    const [messageDetails, setMessageDetails] = useState<Map<number, HumanSuggestedMessageDetail>>(new Map());
    const [loadingDetail, setLoadingDetail] = useState(false);

    const fetchMessages = async () => {
        setLoading(true);
        onLoadingChange?.(true);

        try {
            const response = await fetch(`${BACKEND_URL}/vnog/admin/human_suggested_messages/`, {
                credentials: 'include',
            });

            if (response.ok) {
                const data = await response.json();
                setMessages(data);
            } else {
                toast.error('Failed to fetch human suggested messages');
                setMessages([]);
            }
        } catch (error) {
            toast.error('Error fetching human suggested messages');
            setMessages([]);
        } finally {
            setLoading(false);
            onLoadingChange?.(false);
        }
    };

    const fetchMessageDetail = async (messageId: number) => {
        // Check cache first
        if (messageDetails.has(messageId)) {
            setSelectedMessageId(messageId);
            return;
        }

        setLoadingDetail(true);
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/admin/human_suggested_messages/${messageId}/`, {
                credentials: 'include',
            });

            if (response.ok) {
                const detail = await response.json();
                setMessageDetails(prev => new Map(prev).set(messageId, detail));
                setSelectedMessageId(messageId);
            } else {
                toast.error('Failed to fetch message details');
            }
        } catch (error) {
            toast.error('Error fetching message details');
        } finally {
            setLoadingDetail(false);
        }
    };

    useEffect(() => {
        fetchMessages();
    }, []);

    if (loading) {
        return (
            <div className="px-6 py-4 bg-white rounded-lg shadow">Loading…</div>
        );
    }

    if (messages.length === 0) {
        return (
            <div className="px-6 py-4 bg-white rounded-lg shadow text-gray-500">No human suggested messages yet</div>
        );
    }

    const selectedDetail = selectedMessageId ? messageDetails.get(selectedMessageId) : null;

    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-semibold mb-1">Human Suggested AI Operator Messages</h2>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Left column - List of messages */}
                <div className="lg:col-span-1">
                    <div className="space-y-3">
                        {messages.map((item) => (
                            <div
                                key={item.id}
                                className={`bg-white rounded-lg shadow p-4 cursor-pointer transition-colors ${
                                    selectedMessageId === item.id
                                        ? 'ring-2 ring-blue-500 bg-blue-50'
                                        : 'hover:bg-gray-50'
                                }`}
                                onClick={() => fetchMessageDetail(item.id)}
                            >
                                <div className="text-sm text-gray-500 mb-1">Message</div>
                                <div className="text-sm font-medium text-gray-900 mb-2 break-words line-clamp-3">
                                    {item.message}
                                </div>

                                {item.documentation_name && (
                                    <>
                                        <div className="text-sm text-gray-500 mb-1">Document</div>
                                        <div className="text-sm text-gray-700 mb-2 break-words line-clamp-1">
                                            {item.documentation_name}
                                        </div>
                                    </>
                                )}

                                {item.quote?.exact_document_quotes?.[0] && (
                                    <>
                                        <div className="text-sm text-gray-500 mb-1">Quote</div>
                                        <div className="text-xs text-gray-600 italic mb-2 line-clamp-2">
                                            &quot;{item.quote.exact_document_quotes[0]}&quot;
                                        </div>
                                    </>
                                )}

                                <div className="text-xs text-gray-500">
                                    {new Date(item.created_at).toLocaleDateString()}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right column - Detail view */}
                <div className="lg:col-span-3">
                    {loadingDetail ? (
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="text-center">Loading details...</div>
                        </div>
                    ) : (
                        <DetailView detail={selectedDetail || null} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default HumanSuggestedMessages;
